/**
 * The half of signing up that was missing.
 *
 * Creating a Firebase account and creating a Grow account were never the
 * same act. The first happened on the login page; the second happened in
 * MySQL, by hand, for each of the twenty people who have one. Anybody else
 * who signed up got a real Firebase identity, no row, and a login page that
 * would not let them past — signed in and locked out at once.
 *
 * This is what stands in that gap. It appears whenever somebody is signed in
 * to Firebase and has no garden yet, which covers a fresh email sign-up, a
 * first Google sign-in, and anyone stranded by the old behaviour.
 *
 * It asks nothing. It used to ask for the name (already typed on the sign-up
 * form, or given by Google), the postal code and the country: a plain form
 * before any of Grow was seen. The account is made with the name the sign-in
 * already has, while a line says so; the postcode is asked on Today, under
 * the welcome and the season's painting (TodayPostcode). The name can be
 * changed in the profile. Only if making the account fails is there anything
 * to do here. A failure on our side (a 500, no connection) is the standard
 * (D31): reported, said plainly with the support address, and no Try again;
 * the server's request limit (a 429, B24) says its own calm sentence with the
 * support address and no Try again either (serverFailure.limited); any other
 * refusal keeps its own words and Try again. Either way, sign out.
 *
 * Where they came from goes with it, as kept from their first visit: the
 * share that brought them with when its page was opened (the server credits
 * it only within welcome.REFERRAL_DAYS; audit A73), a founding code, a tagged link's values and which
 * of the owner's own links, Instagram or Facebook, brought them
 * (arrivalSource). The backend keeps them on the account; an open founding
 * code makes it a founding gardener, and the owner's link gives it the
 * follower trial (the backend decides its days). So does the language the
 * sign-in page was in (a Spanish browser, or its "Español" switch), which the
 * account's preferences start in: every screen after, and the welcome email
 * (sent once the ZIP code is given), are in it.
 *
 * Requests: POST /users (api/users createAccount; the email comes from the
 * Firebase token, never the body, and a repeat answers the account already
 * made), then AuthContext's refreshMysqlUser reads it back, and Layout,
 * seeing an account, shows the app. Mounted by Layout only.
 *
 * @module CompleteSignup
 */

import { heldReferral, REFERRAL_AT_KEY, REFERRAL_KEY, REFERRAL_NAME_KEY } from '@/shared/api/shares';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/shared/components/ui/button';
import Spinner from '@/shared/components/ui/Spinner';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { getLanguage } from '@/shared/i18n/i18nSetup';
import { createAccount, SIGNUP_NAME_KEY } from '@/shared/api/users';
import { forgetArrival, keptFoundingCode, keptFrom, keptUtm } from '@/shared/lib/arrivalSource';
import { isRateLimited, isServerFailure, rateLimitedWords, reportServerFailure } from '@/shared/lib/serverFailure';
import { signupOutcome } from '@/shared/context/accountRead';
import { FailureText, ServerFailureNote } from '@/shared/components/ServerFailure';

/**
 * The name to make the account with: the one typed on the sign-up form (kept
 * for this, since the sign-in can arrive before it is on the user), else the
 * sign-in's own (Google's), else the start of the email address.
 *
 * @param displayName - The Firebase user's name, if it has one.
 * @param email - The Firebase user's email, if it has one.
 * @returns The name; '' only when there is nothing to go on.
 */
function nameFor(displayName: string | null | undefined, email: string | null | undefined): string {
  let typed = '';
  try { typed = window.sessionStorage.getItem(SIGNUP_NAME_KEY)?.trim() ?? ''; } catch { /* private mode */ }
  return typed || displayName?.trim() || email?.split('@')[0] || '';
}

/**
 * Makes the Grow account, and says so. If it cannot: the standard failure for
 * one on our side, or its own words and Try again for anything else.
 */
export const CompleteSignup: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const { currentUser, refreshMysqlUser, logout } = useAuth();
  // Why making the account failed: on our side (the standard, no retry), the request limit (its sentence, no retry), or otherwise.
  const [failed, setFailed] = useState<'server' | 'limited' | 'other' | null>(null);
  // What failed on our side, so the note can show the report's reference code (E3).
  const [failure, setFailure] = useState<unknown>(undefined);
  const started = useRef(false);

  /** Makes the account; Layout then shows Today, which asks where the garden is. */
  const make = useCallback(async (): Promise<void> => {
    setFailed(null);
    try {
      // The share that brought them, if a share page was the way in. Read
      // from storage rather than the address, because signup is several
      // screens after the page and the address has long since changed.
      // Sent with when its page was opened: the server credits it only if that was lately (A73).
      const referral = heldReferral();

      await createAccount({
        display_name: nameFor(currentUser?.displayName, currentUser?.email),
        referred_share_code: referral?.code ?? null,
        referred_share_opened_at: referral?.openedAt ?? null,
        // A founding link, the first tagged link and the owner's own link, from the first visit.
        founding_code: keptFoundingCode(),
        ...keptUtm(),
        from: keptFrom(),
        // The sign-in page's language, so the account starts in it.
        language: getLanguage(),
      });
      forgetArrival();
      try {
        window.localStorage.removeItem(REFERRAL_KEY);
        window.localStorage.removeItem(REFERRAL_NAME_KEY);
        window.localStorage.removeItem(REFERRAL_AT_KEY);
        window.sessionStorage.removeItem(SIGNUP_NAME_KEY);
      } catch { /* nothing to clear */ }

      // The account exists now; re-reading it is what swaps this screen for
      // the garden. No navigation needed — Layout decides on mysqlUser.
      // If none comes back, the failure state, not a spinner that never ends (B54).
      // Only a real "no account" is reported here: a failed read was reported by the read itself (C78).
      const outcome = signupOutcome(await refreshMysqlUser());
      if (outcome === 'report') {
        const missing = new Error('Account made (201) but the re-read returned no account');
        reportServerFailure(missing, 'signup');
        setFailure(missing);
        setFailed('server');
      }
    } catch (error) {
      if (isServerFailure(error)) {
        reportServerFailure(error, 'signup');
        setFailure(error);
        setFailed('server');
      } else if (isRateLimited(error)) {
        // The server's request limit: a wait, logged there; its own sentence (B24).
        setFailed('limited');
      } else {
        setFailed('other');
      }
    }
  }, [currentUser, refreshMysqlUser]);

  // Once, as soon as there is somebody signed in to make it for.
  useEffect(() => {
    if (!currentUser || started.current) return;
    started.current = true;
    void make();
  }, [currentUser, make]);

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-background px-4 py-12 text-foreground">
      <div className="w-full max-w-md space-y-6 text-center">
        <p className="font-grow text-2xl font-bold leading-none text-primary">Grow</p>
        {failed === 'server' ? (
          <ServerFailureNote error={failure} where="signup" className="text-muted-foreground" />
        ) : failed === 'limited' ? (
          <p role="status" className="text-sm text-muted-foreground"><FailureText text={rateLimitedWords()} /></p>
        ) : failed === 'other' ? (
          <>
            <p className="text-sm text-destructive">{t('auth.completeSignup.failed')}</p>
            <Button className="w-full" onClick={() => { void make(); }}>{t('auth.completeSignup.tryAgain')}</Button>
          </>
        ) : (
          <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground" role="status">
            <Spinner size={18} />
            {t('auth.completeSignup.saving')}
          </p>
        )}

        {/* A way out for somebody who signed in as the wrong person. Without
            it this screen is a dead end, because the header is not rendered
            until there is an account to render it for. */}
        {failed && (
          <button
            type="button"
            onClick={() => { void logout(); }}
            className="text-sm text-muted-foreground underline underline-offset-4"
          >
            {t('auth.completeSignup.wrongAccount')}
          </button>
        )}
      </div>
    </div>
  );
};
